/**
 * PostPublishVersionDialog — offered AFTER a successful publish (Spec A / D51, A4-2).
 *
 * Publishing freezes the shipped onboarding version. The only reason to mint a new
 * version is so that the *just-published* (now frozen) version stays unchanged
 * while the user keeps editing. That question therefore belongs AFTER publish —
 * never as a blocking pre-publish gate on every deploy. It opens when the deploy
 * flow CLOSES after a success, not on top of the "Published Successfully!" screen.
 *
 * Strategy axis (two-axis parity): when the linked strategy is an unbaked draft,
 * a non-blocking inline "Bake" action is offered so the strategy state that shipped
 * can be locked as an immutable version. A baked strategy was already flipped to
 * `published` by the publish handler.
 *
 * Non-destructive: "Not now" simply dismisses (the reactive freeze-guard banner in
 * the editor still catches a later attempt to edit the frozen version).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { StrategyVersionService } from '@modules/firebase-core/services/StrategyVersionService';
import type { StrategyStatus } from '@segmently/schema-models/strategy';

export interface PostPublishVersionDialogProps {
  open: boolean;
  onClose: () => void;
  /** Name of the version that was just published/frozen (for the message). */
  publishedVersionName?: string;
  /** Mint + switch to a new editable version (usually actions.createVersion). */
  onCreateNewVersion: () => Promise<void>;
  /** Strategy axis — offer a non-blocking Bake when the linked strategy is a draft. */
  projectId?: string;
  strategyId?: string | null;
}

export const PostPublishVersionDialog: React.FC<PostPublishVersionDialogProps> = ({
  open,
  onClose,
  publishedVersionName,
  onCreateNewVersion,
  projectId,
  strategyId,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [strategyStatus, setStrategyStatus] = useState<StrategyStatus | null>(null);
  const [baking, setBaking] = useState(false);
  const [bakedNote, setBakedNote] = useState<string | null>(null);

  // Resolve the linked strategy's lifecycle each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setBakedNote(null);
    setStrategyStatus(null);
    if (!projectId || !strategyId) return;
    let active = true;
    new StrategyService(projectId)
      .get(strategyId)
      .then((strategy) => {
        if (!active) return;
        // A legacy strategy with no lifecycle yet reads as a draft (A8 default).
        setStrategyStatus(((strategy as { status?: StrategyStatus } | null)?.status) ?? 'draft');
      })
      .catch(() => active && setStrategyStatus(null));
    return () => {
      active = false;
    };
  }, [open, projectId, strategyId]);

  const handleCreate = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await onCreateNewVersion();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create a new version');
    } finally {
      setBusy(false);
    }
  }, [onCreateNewVersion, onClose]);

  const handleBakeStrategy = useCallback(async () => {
    if (!projectId || !strategyId) return;
    setBaking(true);
    setError(null);
    try {
      const version = await new StrategyVersionService(projectId).bake(strategyId);
      setStrategyStatus('baked');
      setBakedNote(`Strategy baked as ${version.name ?? version.versionId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bake the linked strategy');
    } finally {
      setBaking(false);
    }
  }, [projectId, strategyId]);

  const showBakeOffer = Boolean(projectId && strategyId && strategyStatus === 'draft');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth data-testid="post-publish-version-dialog">
      <DialogTitle>Published{publishedVersionName ? ` — ${publishedVersionName}` : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <Typography variant="body2">
            This version is now published and read-only. Create a new version to keep
            editing without changing what's live.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            The published version stays frozen so its traffic and analytics are stable.
            You can also do this later — editing the published version will prompt you again.
          </Typography>
          {showBakeOffer && (
            <Alert
              severity="info"
              data-testid="post-publish-bake-offer"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleBakeStrategy}
                  disabled={baking || busy}
                  data-testid="post-publish-bake-strategy"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {baking ? 'Baking…' : 'Bake'}
                </Button>
              }
            >
              The linked strategy has unbaked changes — bake it to lock the shipped
              strategy state as an immutable version.
            </Alert>
          )}
          {bakedNote && (
            <Alert severity="success" data-testid="post-publish-bake-done">
              {bakedNote}
            </Alert>
          )}
          {error && (
            <Alert severity="error" data-testid="post-publish-version-error">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy || baking} data-testid="post-publish-version-dismiss">
          Not now
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={busy || baking}
          data-testid="post-publish-version-create"
        >
          {busy ? 'Creating…' : 'Create new version'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostPublishVersionDialog;
